export interface ByoProductLlmConfiguration {
    id: string;
    organizationId: string;
    tenantId: string;
    folderId: string;
    name: string;
    product: string;
    operationGroupName: string;
    llmConfigurations: ByoLlmConfiguration[];
    enabled: boolean;
    createdAt: Date;
    updatedAt: Date;
}

export interface ByoLlmConfiguration {
    id: string;
    organizationId: string;
    tenantId: string;
    connectionId?: string;
    connectionName?: string;
    connectorType?: LlmConnectorType;
    apiFlavor?: ApiFlavor;
    validConnection: boolean;
    enabled: boolean;
    targetProduct: string;
    targetModel: string;
    defaultModel: string;
    desiredModel: string;
    isCustomAlias?: boolean; // Front-End only, to retain information regarding whether the model name was customized by the user
    connectorTags?: string; // Front-End only, to retain connector tags for resolving connectorTemplate
    connectedModel: string;
    customerModel: string;
    customFieldMappings?: Record<string, string>;
    gatewayParameters?: AiGatewayParameters;
    configurationType?: ByoLlmConfigurationType;
    selfServeByoConfigParameters?: SelfServeByoConfigParameters;
    createdAt: Date;
    updatedAt: Date;
}

interface SelfServeByoConfigParameters {
    connectorTemplate: string;
}

interface AiGatewayParameters {
    apiUrl: string;
    authConnectionId: string;
    authConnectorType?: string; // Front-End only, to retain the IS connector type
    vendorApiType: ModelFamily;
}

// Fields for DataGrid display
type FormattedByoProductLlmConfigurationFields = {
    status: string;
    productIntl: string;
    modelsList: string;
    operationGroupNameIntl: string;
    statusIntl: string;
};

export type ByoProductLlmConfigurationWithFormattedFields = ByoProductLlmConfiguration & FormattedByoProductLlmConfigurationFields;

// Fields for DataGrid display
export interface ByoLlmCompatibilityProbeSummary {
    isAvailable: boolean;
    isCompatible: boolean;
    incompatibilityReason?: string;
    unavailabilityReason?: string;
    vendorRawResponse?: string;
    customFieldMappings?: Record<string, string>;
    isModelNameSimilar?: boolean;
}

export enum ByoLlmConfigurationType {
    Direct = 'Direct',
    AiGateway = 'AiGateway',
    SelfServe = 'SelfServe',
}

export enum LlmConnectorType {
    OpenAi = 'OpenAi',
    AzureOpenAi = 'AzureOpenAi',
    AwsBedrock = 'AwsBedrock',
    AmazonWebServices = 'AmazonWebServices',
    GoogleVertex = 'GoogleVertex',
    OpenAiV1Compatible = 'OpenAiV1Compatible',
    NvidiaNim = 'NvidiaNim',
    Custom = 'Custom',
}

export enum ModelFamily {
    GoogleGemini = 'GoogleGemini',
    MetaLlama = 'MetaLlama',
    AnthropicClaude = 'AnthropicClaude',
    AnthropicMessages = 'AnthropicMessages',
    OpenAi = 'OpenAi',
    Qwen = 'Qwen',
}

export enum ApiFlavor {
    OpenAiChatCompletions = 'OpenAiChatCompletions',
    OpenAiResponses = 'OpenAiResponses',
    OpenAiEmbeddings = 'OpenAiEmbeddings',
    GeminiGenerateContent = 'GeminiGenerateContent',
    GeminiEmbeddings = 'GeminiEmbeddings',
    AwsBedrockInvoke = 'AwsBedrockInvoke',
    AwsBedrockConverse = 'AwsBedrockConverse',
    AnthropicMessages = 'AnthropicMessages',
}

enum AuthConnectorType {
    Okta = 'Okta',
}

export enum LlmConfigurationStatus {
    Connected = 'Connected',
    Failed = 'Failed',
    Disabled = 'Disabled',
    UpgradeNeeded = 'UpgradeNeeded',
}

export const ByoModelIntegrationServiceConnectorTypes = [
    'uipath-openai-openai',
    'uipath-openai-openaiv1compliant',
];

export const IntegrationServiceConnectorTypesToLlmConnectorTypes: { [key in string]: LlmConnectorType } = {
    'uipath-openai-openai': LlmConnectorType.OpenAi,
    'uipath-microsoft-azureopenai': LlmConnectorType.AzureOpenAi,
    'uipath-aws-bedrock': LlmConnectorType.AwsBedrock,
    'uipath-amazon-webservices': LlmConnectorType.AmazonWebServices,
    'uipath-google-vertex': LlmConnectorType.GoogleVertex,
    'uipath-openai-openaiv1compliant': LlmConnectorType.OpenAiV1Compatible,
    'uipath-nvidia-nim': LlmConnectorType.NvidiaNim,
};

export const LlmConnectorTypesToIntegrationServiceConnectorTypes: { [key in LlmConnectorType]: string } = {
    [LlmConnectorType.OpenAi]: 'uipath-openai-openai',
    [LlmConnectorType.AzureOpenAi]: 'uipath-microsoft-azureopenai',
    [LlmConnectorType.AwsBedrock]: 'uipath-aws-bedrock',
    [LlmConnectorType.AmazonWebServices]: 'uipath-amazon-webservices',
    [LlmConnectorType.GoogleVertex]: 'uipath-google-vertex',
    [LlmConnectorType.OpenAiV1Compatible]: 'uipath-openai-openaiv1compliant',
    [LlmConnectorType.NvidiaNim]: 'uipath-nvidia-nim',
    [LlmConnectorType.Custom]: '',
};

export const IntegrationServiceConnectorTypesToAuthConnectorTypes: { [key in string]: AuthConnectorType } = { 'uipath-okta-okta': AuthConnectorType.Okta };

export const LlmConnectorTypesToModelFamily: { [key in LlmConnectorType]: ModelFamily } = {
    [LlmConnectorType.OpenAi]: ModelFamily.OpenAi,
    [LlmConnectorType.AzureOpenAi]: ModelFamily.OpenAi,
    [LlmConnectorType.AwsBedrock]: ModelFamily.AnthropicClaude,
    [LlmConnectorType.AmazonWebServices]: ModelFamily.AnthropicClaude,
    [LlmConnectorType.GoogleVertex]: ModelFamily.GoogleGemini,
    [LlmConnectorType.OpenAiV1Compatible]: ModelFamily.OpenAi,
    [LlmConnectorType.NvidiaNim]: ModelFamily.OpenAi,
    [LlmConnectorType.Custom]: ModelFamily.OpenAi,
};

export const LlmConnectorTypesToApiFlavors: { [key in LlmConnectorType]: ApiFlavor[] } = {
    [LlmConnectorType.OpenAi]: [ ApiFlavor.OpenAiChatCompletions, ApiFlavor.OpenAiResponses, ApiFlavor.OpenAiEmbeddings ],
    [LlmConnectorType.AzureOpenAi]: [ ApiFlavor.OpenAiChatCompletions, ApiFlavor.OpenAiResponses, ApiFlavor.OpenAiEmbeddings ],
    [LlmConnectorType.AwsBedrock]: [],
    [LlmConnectorType.AmazonWebServices]: [ ApiFlavor.AwsBedrockInvoke, ApiFlavor.AwsBedrockConverse ],
    [LlmConnectorType.GoogleVertex]: [ ApiFlavor.GeminiGenerateContent, ApiFlavor.GeminiEmbeddings ],
    [LlmConnectorType.OpenAiV1Compatible]: [ ApiFlavor.OpenAiChatCompletions, ApiFlavor.OpenAiResponses, ApiFlavor.OpenAiEmbeddings ],
    [LlmConnectorType.NvidiaNim]: [ ApiFlavor.OpenAiChatCompletions, ApiFlavor.OpenAiResponses, ApiFlavor.OpenAiEmbeddings ],
    [LlmConnectorType.Custom]: Object.values(ApiFlavor),
};

export const ConfigurationStatusTranslationKeys: { [key in LlmConfigurationStatus]: string } = {
    [LlmConfigurationStatus.Connected]: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_TABLE_STATUS_CONNECTED',
    [LlmConfigurationStatus.Failed]: 'CLIENT_FAILED',
    [LlmConfigurationStatus.Disabled]: 'CLIENT_DISABLED',
    [LlmConfigurationStatus.UpgradeNeeded]: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_TABLE_STATUS_UPGRADE_NEEDED',
};

export enum ModelsConfigurationOption {
    AnyModel = 'AnyModel',
    AnyModelWithOwnAdditions = 'AnyModelWithOwnAdditions',
    AllModels = 'AllModels',
}

export interface ByoLlmProbe {
    apiFlavor: string;
    probeId: string;
    warningCodes?: string[];
}

interface ByoLlmModelAlternative {
    model: string;
    usageEndDate?: string;
    probes?: ByoLlmProbe[];
}

export interface ByoLlmModelConfiguration {
    model: string;
    usageStartDate: string;
    usageEndDate?: string;
    alternatives?: string[];
    alternativesWithProbes?: ByoLlmModelAlternative[];
    genericAlternativeProbes?: ByoLlmProbe[];
    probes?: ByoLlmProbe[];
    titleTranslationKey?: string;
    descriptionTranslationKey?: string;
}

export interface ByoOperationGroupConfiguration {
    operationCodes: string[];
    modelsConfigurationOption: ModelsConfigurationOption;
    models: ByoLlmModelConfiguration[];
    probes?: ByoLlmProbe[];
    descriptionTranslationKey?: string;
}

export enum KnownRequestingProducts {
    UiAgents = 'UIA',
    IXP = 'COMMUNICATIONSMINING',
}

export enum KnownProductFeatureNames {
    UiaScreenPlay = 'UIA-AGENT',
    UiaSemanticTargeting = 'UIA-SEMANTIC-TARGETING',
}

export enum ByoFormErrorTypes {
    RequiredField = 'requiredField',
    ModelNameOverlap = 'modelNameOverlap',
}

export enum CustomConnectorTemplate {
    AzureOpenAi = 'AzureOpenAi',
    OpenAiV1Compliant = 'OpenAiV1Compliant',
    GoogleVertexAi = 'GoogleVertexAi',
    AmazonBedrock = 'AmazonBedrock',
    CustomAiGateway = 'CustomAiGateway',
}

export const CustomConnectorTemplateTranslationKeys: { [key in CustomConnectorTemplate]: string } = {
    [CustomConnectorTemplate.AzureOpenAi]: 'CLIENT_BYO_LLM_TEMPLATE_AZURE_OPENAI',
    [CustomConnectorTemplate.OpenAiV1Compliant]: 'CLIENT_BYO_LLM_TEMPLATE_OPENAI_V1_COMPATIBLE',
    [CustomConnectorTemplate.GoogleVertexAi]: 'CLIENT_BYO_LLM_TEMPLATE_VERTEXAI',
    [CustomConnectorTemplate.AmazonBedrock]: 'CLIENT_BYO_LLM_TEMPLATE_AWS_BEDROCK',
    [CustomConnectorTemplate.CustomAiGateway]: 'CLIENT_BYO_LLM_TEMPLATE_CUSTOM_AI_GATEWAY',
};

export const CustomConnectorTemplateUrls: { [key in CustomConnectorTemplate]: string } = {
    [CustomConnectorTemplate.AzureOpenAi]: 'connectorbuilder_/create?k=template-uipath-microsoftazureopenai',
    [CustomConnectorTemplate.OpenAiV1Compliant]: 'connectorbuilder_/create?k=template-uipath-openaiv1compliant',
    [CustomConnectorTemplate.GoogleVertexAi]: 'connectorbuilder_/create?k=template-uipath-googlevertexai',
    [CustomConnectorTemplate.AmazonBedrock]: 'connectorbuilder_/create?k=template-uipath-amazonbedrock',
    [CustomConnectorTemplate.CustomAiGateway]: 'connectorbuilder_/create?k=template-uipath-aigateway',
};

export const ApiFlavorToConnectorTemplates: { [key in ApiFlavor]: CustomConnectorTemplate[] } = {
    [ApiFlavor.OpenAiChatCompletions]: [ CustomConnectorTemplate.AzureOpenAi, CustomConnectorTemplate.OpenAiV1Compliant ],
    [ApiFlavor.OpenAiResponses]: [ CustomConnectorTemplate.AzureOpenAi, CustomConnectorTemplate.OpenAiV1Compliant ],
    [ApiFlavor.OpenAiEmbeddings]: [ CustomConnectorTemplate.AzureOpenAi, CustomConnectorTemplate.OpenAiV1Compliant ],
    [ApiFlavor.GeminiGenerateContent]: [ CustomConnectorTemplate.GoogleVertexAi ],
    [ApiFlavor.GeminiEmbeddings]: [ CustomConnectorTemplate.GoogleVertexAi ],
    [ApiFlavor.AwsBedrockInvoke]: [ CustomConnectorTemplate.AmazonBedrock ],
    [ApiFlavor.AwsBedrockConverse]: [ CustomConnectorTemplate.AmazonBedrock ],
    [ApiFlavor.AnthropicMessages]: [],
};

export const ApiFlavorToCustomConnectorTags: { [key in ApiFlavor]: string[] } = {
    [ApiFlavor.OpenAiChatCompletions]: [ 'aigateway-openai-chat-completions', 'azure-openai', 'openai-v1-compliant' ],
    [ApiFlavor.OpenAiResponses]: [ 'aigateway-openai-responses', 'azure-openai', 'openai-v1-compliant' ],
    [ApiFlavor.OpenAiEmbeddings]: [ 'aigateway-openai-embeddings', 'azure-openai', 'openai-v1-compliant' ],
    [ApiFlavor.GeminiGenerateContent]: [ 'aigateway-gemini-generatecontent', 'google-vertexai' ],
    [ApiFlavor.GeminiEmbeddings]: [ 'aigateway-gemini-embeddings', 'google-vertexai' ],
    [ApiFlavor.AwsBedrockInvoke]: [ 'aigateway-aws-bedrock-invoke-model', 'amazon-web-services' ],
    [ApiFlavor.AwsBedrockConverse]: [ 'aigateway-aws-bedrock-converse', 'amazon-web-services' ],
    [ApiFlavor.AnthropicMessages]: [ 'aigateway-anthropic-messages' ],
};

export const CustomConnectorTagToTemplate: Record<string, CustomConnectorTemplate> = {
    // Tags for custom connectors built with specific templates
    'azure-openai': CustomConnectorTemplate.AzureOpenAi,
    'openai-v1-compliant': CustomConnectorTemplate.OpenAiV1Compliant,
    'google-vertexai': CustomConnectorTemplate.GoogleVertexAi,
    'amazon-web-services': CustomConnectorTemplate.AmazonBedrock,
    // Tags for AI Gateway custom connectors
    'aigateway-openai-chat-completions': CustomConnectorTemplate.CustomAiGateway,
    'aigateway-openai-responses': CustomConnectorTemplate.CustomAiGateway,
    'aigateway-openai-embeddings': CustomConnectorTemplate.CustomAiGateway,
    'aigateway-gemini-generatecontent': CustomConnectorTemplate.CustomAiGateway,
    'aigateway-gemini-embeddings': CustomConnectorTemplate.CustomAiGateway,
    'aigateway-aws-bedrock-invoke-model': CustomConnectorTemplate.CustomAiGateway,
    'aigateway-aws-bedrock-converse': CustomConnectorTemplate.CustomAiGateway,
};
